// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { SyntheticEvent } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";
import { FontIcon, Label, Spinner, SpinnerSize } from "@fluentui/react";
import { strings, interpolate } from "../../../../common/strings";
import { getPrimaryRedTheme } from "../../../../common/themes";
import IProjectActions, * as projectActions from "../../../../redux/actions/projectActions";
import IApplicationActions, * as applicationActions from "../../../../redux/actions/applicationActions";
import IAppTitleActions, * as appTitleActions from "../../../../redux/actions/appTitleActions";
import { CloudFilePicker } from "../../common/cloudFilePicker/cloudFilePicker";
import FilePicker from "../../common/filePicker/filePicker";
import CondensedList from "../../common/condensedList/condensedList";
import Confirm from "../../common/confirm/confirm";
import "./homePage.scss";
import RecentProjectItem from "./recentProjectItem";
import { constants } from "../../../../common/constants";
import {
    IApplicationState,
    IConnection,
    IProject,
    ErrorCode,
    AppError,
    IAppSettings,
    IDeleteProjectCommand,
    ICreateProjectCommand,
    ICreateSecurityTokensCommand,
    ISecurityToken,
    IGetProjectTokenCommand,
    ICreateProjectTokenCommand
} from "../../../../models/applicationState";
import { StorageProviderFactory } from "../../../../providers/storage/storageProviderFactory";
import { decryptProject } from "../../../../common/utils";
import { toast } from "react-toastify";
import { isElectron } from "../../../../common/hostProcess";
import ProjectService from "../../../../services/projectService";
import { HomeProjectView } from "./homeProjectView";
import {
    getCookieAdmin,
    getCookieEmail,
    parseEndpointByType,
} from "../../common/jsonSchemaFormHelper";
import axios from "axios";
import { generateKey } from "../../../../common/crypto";
/*import FixProjectModal from "./fixProjectModal";*/

export interface IHomePageProps
    extends RouteComponentProps,
        React.Props<HomePage> {
    recentProjects: IProject[];
    connections: IConnection[];
    actions: IProjectActions;
    applicationActions: IApplicationActions;
    appSettings: IAppSettings;
    project: IProject;
    appTitleActions: IAppTitleActions;
    widgetMode?: boolean;
}

export interface IHomePageState {
    cloudPickerOpen: boolean;
    loading: boolean;
    brokenProjectId: string;
    brokenProjectName: string;
    //showFixProjectModal: boolean;
    brokenProjectErrorMessage: string;
    fixProjectLoading: boolean;
}

function mapStateToProps(state: IApplicationState) {
    return {
        recentProjects: state.recentProjects,
        connections: state.connections,
        appSettings: state.appSettings,
        project: state.currentProject,
        widgetMode: state.appSettings.widgetMode
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(projectActions, dispatch),
        applicationActions: bindActionCreators(applicationActions, dispatch),
        appTitleActions: bindActionCreators(appTitleActions, dispatch),
    };
}

@connect(mapStateToProps, mapDispatchToProps)
export default class HomePage extends React.Component<
    IHomePageProps,
    IHomePageState
> {
    public state: IHomePageState = {
        cloudPickerOpen: false,
        loading: false,
        brokenProjectId: '', 
        brokenProjectName: '',
        brokenProjectErrorMessage: '',
        //showFixProjectModal: false, 
        fixProjectLoading: false
    };
    private homeProjectViewRef: React.RefObject<HomeProjectView> =
        React.createRef();
    private filePicker: React.RefObject<FilePicker> = React.createRef();
    private deleteConfirmRef = React.createRef<Confirm>();
    private cloudFilePickerRef = React.createRef<CloudFilePicker>();
    private importConfirmRef: React.RefObject<Confirm> = React.createRef();
    projectName: string;

    public async componentDidMount() {
        this.props.appTitleActions.setTitle("");

        document.title = strings.homePage.title + " - " + strings.appName;

        let url = new URL(window.location.href);
        let key = url.searchParams.get("key");
        this.projectName = url.searchParams.get("projectName");

        let returnUrlProjectId = '';
        let returnUrlAction = '';
        let returnUrl = url.searchParams.get("returnUrl");
        if (returnUrl) {
            let returnUrlParams = returnUrl.split('/');
            if (returnUrlParams.length > 2) {
                returnUrlProjectId = returnUrlParams[1];
            }
            if (returnUrlParams.length > 3) {
                returnUrlAction = returnUrlParams[2];
            }            
        }        
    }

    public render() {
        const { widgetMode } = this.props.appSettings;
        return (
            <div className="app-homepage" id="pageHome">
                {this.state.loading && (
                    <div className="project-saving">
                        <div className="project-saving-spinner">
                            <Label className="p-0"></Label>
                            <Spinner
                                size={SpinnerSize.large}
                                label="Loading Projects..."
                                ariaLive="assertive"
                                labelPosition="right"
                            />
                        </div>
                    </div>
                )}
                {this.state.fixProjectLoading && (
                    <div className="project-saving">
                        <div className="project-saving-spinner">
                            <Label className="p-0"></Label>
                            <Spinner
                                size={SpinnerSize.large}
                                label="Opening Project..."
                                ariaLive="assertive"
                                labelPosition="right"
                            />
                        </div>
                    </div>
                )}
                <div className="app-homepage-main">
                    <ul>
                        <li style={{ position: "relative" }}>
                            <div className="banner">
                                <img
                                    alt=""
                                    className="logo"
                                    src={require("../../../../assets/images/logo.png")}
                                ></img>
                            </div>
                            {!getCookieAdmin() && (
                                <a                                    
                                    className="primary-link"
                                    role="button"
                                    href="mailto:tim@stackdc.com"
                                >
                                    <FontIcon
                                        iconName="rocket"
                                        className="icon-7x"
                                    />
                                    <div className="title">
                                        {strings.homePage.trainWithLabels.title}
                                    </div>
                                    <div className="description">
                                        Please contact administrator for access to hatchet integration tool.
                                    </div>
                                </a>
                            )}                            
                        </li>
                        <CloudFilePicker
                            ref={this.cloudFilePickerRef}
                            connections={this.props.connections}
                            onSubmit={(content, sharedToken?) =>
                                this.loadSelectedProject(
                                    JSON.parse(content),
                                    sharedToken
                                )
                            }
                            fileExtension={constants.projectFileExtension}
                        />
                    </ul>
                </div>
                
                {!getCookieAdmin() && (
                    <>
                        <Confirm
                            title="Delete Project"
                            ref={this.deleteConfirmRef as any}
                            message={(project: IProject) =>
                                `${strings.homePage.deleteProject.confirmation} ${project.name}?`
                            }
                            confirmButtonTheme={getPrimaryRedTheme()}
                            onConfirm={this.deleteProject}
                        />
                        <HomeProjectView
                            ref={this.homeProjectViewRef}
                            recentProjects={this.props.recentProjects}
                            connections={this.props.connections}
                            createNewProject={this.createNewProject}
                            onProjectFileUpload={this.onProjectFileUploadError}
                            onProjectFileUploadError={
                                this.onProjectFileUploadError
                            }
                            onOpenCloudProjectClick={
                                this.onOpenCloudProjectClick
                            }
                            loadSelectedProject={this.loadSelectedProject}
                            freshLoadSelectedProject={
                                this.freshLoadSelectedProject
                            }
                            deleteProject={this.deleteProject}
                        />
                    </>
                )}
            </div>
        );
    }

    private saveSecurityTokens = (email: string) => {
        const tokens = JSON.stringify(this.props.appSettings);
        axios
            .post(parseEndpointByType("CreateSecurityTokens"), {
                email: email,
                tokens: tokens,
            } as ICreateSecurityTokensCommand)
            .then((response: any) => {
                if (!response.data.successful) {
                    toast.error(interpolate(response.data.errorMessage, {}));
                }
            })
            .catch((error) => { });
        // .finally(() => {
        //     this.setState({
        //         loading: false,
        //     });
        // });
    }

    private createNewProject = (e: SyntheticEvent) => {
        this.props.actions.closeProject();
        this.props.history.push("/projects/create");

        e.preventDefault();
    };

    private onPrebuiltClicked = () => {
        this.props.history.push("/prebuilts-analyze");
    };

    private onUseLayoutToGetTextAndTAblesClicked = () => {
        this.props.history.push("/layout-analyze");
    };

    private onTrainAndUseAModelWithLables = () => {
        this.homeProjectViewRef.current.open();
    };

    private onOpenCloudProjectClick = () => {
        this.homeProjectViewRef.current.close();
        this.cloudFilePickerRef.current.open();
    };

    private loadSelectedProject = async (
        project: IProject,
        sharedToken?: {},
        savedToken?: {}
    ) => {
        let savedProjectToken = null;
        if (!savedToken) {
            const projectTokenKey = await this.getProjectToken(getCookieEmail(), project.id);
            if (projectTokenKey) {
                savedToken = {
                    name: project.securityToken,
                    key: projectTokenKey
                };
                savedProjectToken = savedToken;
            }
            else {
                this.startRepair(project);
                return;
            }
        }
        else {
            savedProjectToken = savedToken;
        }
        try {
            const loadedProject = await this.props.actions.loadProject(
                project,
                sharedToken,
                savedProjectToken
            );
            if (loadedProject !== null) {
                this.props.history.push(`/projects/${project.id}/edit`);
            }
        } catch (error) {
            if (
                error instanceof AppError &&
                error.errorCode === ErrorCode.SecurityTokenNotFound
            ) {
                toast.error(strings.errors.securityTokenNotFound.message, {
                    autoClose: 5000,
                });
            }
            if (
                error instanceof AppError &&
                error.errorCode === ErrorCode.ProjectInvalidSecurityToken
            ) {
                toast.error(
                    strings.errors.projectInvalidSecurityToken.message,
                    { autoClose: 5000 }
                );
            }
        }
    };

    private getProjectToken = async (email: string, projectId: string): Promise<string> => {
        const response = await axios
            .post(parseEndpointByType("GetProjectToken"), {
                email: email,
                projectId: projectId
            } as IGetProjectTokenCommand);
        if (response.data.successful) {
            return response.data.stringValue1;
        }
        else {
            return "";
        }
    }

    // private getProjectSas = async (email: string, projectId: string): Promise<string> => {
    //     const response = await axios
    //         .post(parseEndpointByType("getProjectSas"), {
    //             email: email,
    //             projectId: projectId
    //         } as IGetProjectTokenCommand);
    //     if (response.data.successful) {
    //         return response.data.stringValue1;
    //     }
    //     else {
    //         return "";
    //     }
    // }

    // private createSecurityToken = (project: IProject): ISecurityToken => {
    //     const tokenPrefix = `${project.name} Token`;
    //     let tokenCountWithTheSamePrefix = 0
    //     let securityToken = {} as ISecurityToken

    //     if (this.props.appSettings.securityTokens) {
    //         let securityToken = this.props.appSettings.securityTokens
    //             .find((st) => st.name === project.securityToken);

    //         tokenCountWithTheSamePrefix = this.props.appSettings.securityTokens
    //             .filter((st) => st.name.startsWith(tokenPrefix))
    //             .length;
    //     }

    //     const tokenSuffix = tokenCountWithTheSamePrefix === 0 ? `` : ` [${tokenCountWithTheSamePrefix}]`;
    //     securityToken = {
    //         name: `${tokenPrefix}${tokenSuffix}`,
    //         key: generateKey(),
    //     };
    //     return securityToken;
    // }

    // private saveProjectToken(projectId: string, email: string, tokenKey: string) {
    //     axios
    //         .post(parseEndpointByType("CreateProjectToken"), {
    //             email: email,
    //             securityToken: tokenKey,
    //             projectId: projectId
    //         } as ICreateProjectTokenCommand)
    //         .then((response: any) => {
    //             if (!response.data.successful) {
    //                 toast.error(interpolate(response.data.errorMessage, {}));
    //             }
    //         })
    //         .catch((error) => { });
    // }

    private startRepair = async (project: IProject) => {
        //const promisedSetState = (newState) => new Promise(resolve => {
        //    this.setState(newState
        //        , () => {
        //            resolve(null);
        //        });
        //});

        //const { brokenProjectId } = this.state;
        const data = await this.getProjectSas(getCookieEmail(), project.id);
        if (data) {
            //let project = this.props.recentProjects.find(r => r.id === project.id);
            //if (project) {
            //}
            const securityToken = this.createSecurityToken(project);
            if (securityToken) {
                const modifiedProject = {
                    ...project,
                    apiKey: process.env.REACT_APP_TEMP_API_KEY,
                    securityToken: data.stringValue2,
                    sourceConnection: {
                        ...project.sourceConnection,
                        providerOptions: {
                            sas: data.stringValue1
                        }
                    },
                };
                await this.props.actions.saveProject(modifiedProject, false, false, securityToken);
                await this.saveProjectToken(modifiedProject.id, getCookieEmail(), securityToken.key);
                //await promisedSetState({ fixProjectLoading: false });
                const self = this;                    
                setTimeout(async function () {
                    const updatedProject = self.props.recentProjects.find(r => r.id === project.id);
                    await self.freshLoadSelectedProject(updatedProject);                    
                }, 3000);
            }
        }
    }

    private createSecurityToken = (project: IProject): ISecurityToken => {
        const tokenPrefix = `${project.name} Token`;
        let tokenCountWithTheSamePrefix = 0
        let securityToken = {} as ISecurityToken

        if (this.props.appSettings.securityTokens) {
            let securityToken = this.props.appSettings.securityTokens
                .find((st) => st.name === project.securityToken);

            tokenCountWithTheSamePrefix = this.props.appSettings.securityTokens
                .filter((st) => st.name.startsWith(tokenPrefix))
                .length;
        }

        const tokenSuffix = tokenCountWithTheSamePrefix === 0 ? `` : ` [${tokenCountWithTheSamePrefix}]`;
        securityToken = {
            name: `${tokenPrefix}${tokenSuffix}`,
            key: generateKey(),
        };
        return securityToken;
    }

    private getProjectSas = async (email: string, projectId: string): Promise<any> => {
        const response = await axios
            .post(parseEndpointByType("getProjectSas"), {
                email: email,
                projectId: projectId
            } as IGetProjectTokenCommand);
        if (response.data.successful) {
            return response.data;
        }
        else {
            return "";
        }
    }

    private saveProjectToken = (projectId: string, email: string, tokenKey: string) => {
        axios
            .post(parseEndpointByType("CreateProjectToken"), {
                email: email,
                securityToken: tokenKey,
                projectId: projectId
            } as ICreateProjectTokenCommand)
            .then((response: any) => {
                if (!response.data.successful) {
                    toast.error(interpolate(response.data.errorMessage, {}));
                }
            })
            .catch((error) => { });
    }

    //private verifyProjectToken = async (project: IProject) => {

    //    const promisedSetState = (newState) => new Promise(resolve => {
    //        this.setState(
    //            newState
    //        , async () => {
    //            await this.startRepair();
    //            resolve(null);
    //        })
    //    });

    //    let projectToken = this.props.appSettings.securityTokens.find(
    //        (securityToken) => securityToken.name === project.securityToken
    //    );
    //    if (!projectToken) {
    //        const projectTokenKey = await this.getProjectToken(getCookieEmail(), project.id);
    //        if (!projectTokenKey) {
    //            await promisedSetState({ brokenProjectId: project.id, fixProjectLoading: true });
    //            //await this.setState({
    //            //    brokenProjectId: project.id
    //            //}, async () => {
    //            //    await this.startRepair();
    //            //});
    //        }            
    //    }
    //}

    private freshLoadSelectedProject = async (project: IProject) => {
        //await this.verifyProjectToken(project);
        // Lookup security token used to decrypt project settings
        this.setState({
            fixProjectLoading: true
        });
        const tokenName = `${project.name} Token`;
        
        let projectToken = this.props.appSettings.securityTokens.find(
            (securityToken) => securityToken.name === tokenName
        );

        let savedToken: ISecurityToken = null;
        let projectSas = '';
        if (!projectToken) {
            const projectTokenKey = await this.getProjectToken(getCookieEmail(), project.id);
            if (projectTokenKey) {
                savedToken = {
                    name: project.securityToken,
                    key: projectTokenKey
                };
                projectToken = savedToken;
            }
            else {
                this.startRepair(project);
                return;
            }
        }

        // Load project from storage provider to keep the project in latest state
        const decryptedProject = await decryptProject(project, projectToken);
        const storageProvider = StorageProviderFactory.createFromConnection(
            decryptedProject.sourceConnection
        );
        try {
            let projectStr: string;
            try {
                const projectService = new ProjectService();
                if (
                    !(await projectService.isValidProjectConnection(
                        decryptedProject
                    ))
                ) {
                    return;
                }
                projectStr = await storageProvider.readText(
                    `${decryptedProject.name}${constants.projectFileExtension}`
                );
            } catch (err) {
                if (
                    err instanceof AppError &&
                    err.errorCode === ErrorCode.BlobContainerIONotFound
                ) {
                    // try old file extension
                    projectStr = await storageProvider.readText(
                        `${decryptedProject.name}${constants.projectFileExtensionOld}`
                    );
                } else {
                    throw err;
                }
            }
            const selectedProject = {
                ...JSON.parse(projectStr),
                sourceConnection: project.sourceConnection,
            };
            await this.loadSelectedProject(selectedProject, null, savedToken);
        } catch (err) {
            if (
                err instanceof AppError &&
                err.errorCode === ErrorCode.BlobContainerIONotFound
            ) {
                const reason = interpolate(
                    strings.errors.projectNotFound.message,
                    {
                        file: `${project.name}${constants.projectFileExtension}`,
                        container: project.sourceConnection.name,
                    }
                );
                toast.error(reason, { autoClose: false });
                return;
            }
            throw err;
        }
        finally {
            this.setState({
                fixProjectLoading: false
            });
        }
    };

    private deleteProject = async (project: IProject) => {
        this.setState({
            loading: true
        });
        try {
            await this.deleteDbProject(project);
            //await this.props.actions.deleteProject(project);
            await this.props.applicationActions.loadAppSettings();
            await this.props.actions.loadProjects(true);
            toast.info(interpolate(strings.homePage.messages.deleteSuccess, { project }));
        } catch (error) {
            if (
                error instanceof AppError &&
                error.errorCode === ErrorCode.SecurityTokenNotFound
            ) {
                toast.error(error.message, { autoClose: false });
            } else {
                throw error;
            }
        }
        finally {
            this.setState({
                loading: false
            });
        }
    };

    private deleteDbProject = async (project: IProject) => {
        var result = await axios.post(parseEndpointByType("DeleteProject"), {
            email: getCookieEmail(),
            // tslint:disable-next-line:radix
            id: parseInt(project.id),
        } as IDeleteProjectCommand);        
    };

    private onProjectFileUpload = async (e, project) => {
        let projectJson: IProject;

        try {
            projectJson = JSON.parse(project.content);
        } catch (error) {
            throw new AppError(
                ErrorCode.ProjectInvalidJson,
                "Error parsing JSON"
            );
        }

        if (projectJson.name === null || projectJson.name === undefined) {
            try {
                await this.importConfirmRef.current.open(project);
            } catch (e) {
                throw new Error(e.message);
            }
        } else {
            await this.loadSelectedProject(projectJson);
        }
    };

    private onProjectFileUploadError = (e, error: any) => {
        if (error instanceof AppError) {
            throw error;
        }
    };
}
